/**
 * VerticalStepper — stepper vertical du profil Particulier.
 *
 * Pattern : rail brand vertical avec dots numérotés, cards à droite,
 * layout 2 colonnes sticky/scroll (titre/desc à gauche figés, liste à droite).
 *
 * Utilisé pour les sections "Comment ça se passe" / "Démarches" / "Process".
 */

import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Container } from '../../layout/Container';
import { Reveal } from '../../motion/Reveal';
import { cn } from '../../../lib/cn';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  type ParticulierStep,
} from '../../../config/audience/particulier.ui';

export interface ParticulierVerticalStepperProps {
  /** Eyebrow section */
  eyebrow: string;
  /** Titre H2 */
  title: ReactNode;
  /** Description (sticky à gauche) */
  description?: ReactNode;
  /** Liste des steps */
  steps: ParticulierStep[];
  /** Lien d'action sous la description (CTA) */
  cta?: { label: string; href: string };
  /** Surcharger les classes de section */
  sectionClass?: string;
  /** Variante "sur fond alterné stone-50" (par défaut : oui) */
  alternateBg?: boolean;
}

export function ParticulierVerticalStepper({
  eyebrow,
  title,
  description,
  steps,
  cta,
  sectionClass,
  alternateBg = true,
}: ParticulierVerticalStepperProps) {
  return (
    <section
      className={cn(
        PARTICULIER_SECTION.standard,
        alternateBg ? PARTICULIER_SECTION.alternateBg : null,
        sectionClass,
      )}
    >
      <Container>
        <div className="mx-auto grid max-w-5xl gap-12 lg:grid-cols-[1fr_2fr] lg:gap-16">
          <Reveal>
            <div className="lg:sticky lg:top-28 lg:self-start">
              <p className="eyebrow text-stone-500">{eyebrow}</p>
              <h2 className="mt-3 font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-tight text-ink text-balance">
                {title}
              </h2>
              {description && (
                <div className="mt-4 max-w-md text-[14.5px] leading-relaxed text-stone-600">
                  {description}
                </div>
              )}
              {cta && (
                <a
                  href={cta.href}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 transition-colors hover:text-brand-800"
                >
                  {cta.label}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </a>
              )}
            </div>
          </Reveal>

          <div className="relative">
            <div
              aria-hidden
              className="absolute left-[15px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-brand-300 via-brand-200 to-transparent"
            />

            <ol className="space-y-6">
              {steps.map((step, idx) => (
                <Reveal key={step.title + idx} delay={idx * 0.06} as="div">
                  <li className="relative flex gap-5">
                    <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-paper ring-2 ring-brand-200">
                      <span
                        className={cn(
                          'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white tabular-nums',
                          PARTICULIER_GRADIENT.stepDot,
                        )}
                      >
                        {idx + 1}
                      </span>
                    </span>

                    <div className="min-w-0 flex-1 rounded-2xl border border-stone-200/80 bg-paper p-5 shadow-[0_1px_3px_rgba(10,10,10,.03)] transition-all duration-150 ease-out hover:border-stone-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,10,.08)]">
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                        {step.eyebrow ?? `Étape ${String(idx + 1).padStart(2, '0')}`}
                      </p>
                      <p className="mt-1 font-display text-base font-semibold text-ink md:text-lg">
                        {step.title}
                      </p>
                      <p className="mt-2 text-[14px] leading-relaxed text-stone-600">
                        {step.description}
                      </p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </Container>
    </section>
  );
}
